/**
 * shared/useSocket.js  (copiar para hooks/ em cada frontend)
 *
 * FIXES:
 *  - BUG-006: socket era criado no module scope (fora do componente)
 *    → múltiplas conexões acumulavam durante HMR em dev
 *    → socket persistia após logout
 *  - Socket só conecta quando token existe
 *  - Cleanup correcto no unmount
 *  - Suporta subscrição dinâmica de eventos via on()
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../constants'; // shared/constants.js

export const useSocket = () => {
  const socketRef    = useRef(null);
  const [isConnected, setIsConnected]   = useState(false);
  const [lastMessage, setLastMessage]   = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return; // não conectar sem token

    socketRef.current = io(SOCKET_URL, {
      autoConnect: true,
      auth: (cb) => cb({ token: localStorage.getItem('authToken') }),
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    const socket = socketRef.current;

    socket.on('connect',    () => setIsConnected(true));
    socket.on('disconnect', () => setIsConnected(false));

    // Eventos genéricos — componentes subscrevem via on()
    const TRACKED_EVENTS = [
      'admin_update',
      'new_request',
      'new_dispute',
      'worker_status_change',
      'request_accepted',
      'notification',
    ];

    TRACKED_EVENTS.forEach((event) => {
      socket.on(event, (data) => {
        setLastMessage({ type: event, data, timestamp: Date.now() });
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []); // sem dependências — socket criado uma vez por mount

  const emit = useCallback((event, data) => {
    socketRef.current?.emit(event, data);
  }, []);

  /** Subscever a um evento específico. Retorna função de cleanup. */
  const on = useCallback((event, callback) => {
    socketRef.current?.on(event, callback);
    return () => socketRef.current?.off(event, callback);
  }, []);

  return { isConnected, lastMessage, emit, on };
};
